import config from '@/config';
import utils from '@/app/utils/utils';
import cities from '@/utils/cities-top';
import { find } from 'lodash-es';
import { buildQuery } from '@/utils/url';
import axios from 'axios';
import type { LocationQuery, Router } from 'vue-router';
import { FiltersSchema, type FiltersType } from '@/schemas/filtersSchema';
import logger from '@/app/logger';
import featureGroups, {
  getFeatureGroupById,
  type FeatureGroupFiltersType,
  type FeatureGroupIdType,
  type FilterViewType,
} from '@/config/featureGroups';
import { CustomError } from '@/utils/errors';
import { useFilterStore } from '@/app/pinia/filterStore';
import { SearchUrlSchema } from '@/schemas/urlSchema';
import { dropNonSchemaParams } from '@/schemas/schemaUtils';
import type { RouteLike } from '@/app/composables/useSafeRoute';

interface PathFilters {
  mode?: keyof FeatureGroupFiltersType;
  featureGroup?: FeatureGroupIdType;
  cityId?: string[];
}
export type LocationFilters = {
  cityId?: string[];
  countyId?: string[];
  districtId?: string[];
  streetId?: string[];
  regionId?: string[];
  postalCode?: string[];
};
const locationFilterKeys: (keyof LocationFilters)[] = [
  'cityId',
  'countyId',
  'districtId',
  'streetId',
  'regionId',
  'postalCode',
];
const nonUserFilters = ['mode', 'featureGroup', 'orderBy', 'order'];
export const possibleLocationUrlParams = ['districtId', 'streetId', 'cityId', 'countyId', 'regionId', 'postalCode'];
export const regionTypeMapping = {
  county: 1,
  city: 2,
  region: 3,
  district: 4,
  street: 5,
};

export default {
  hasActiveUserFilters(filters: any): boolean {
    if (!filters) {
      return false;
    }
    const nonEmptyUserFilters = Object.keys(filters).filter((key) => !nonUserFilters.includes(key) && !!filters[key]);
    return nonEmptyUserFilters.length > 0;
  },
  getLocationFilters(filters: FiltersType): LocationFilters {
    const locationFilters: LocationFilters = {};
    locationFilterKeys.forEach((key) => {
      if (key in filters) {
        locationFilters[key] = filters[key];
      }
    });
    return locationFilters;
  },
  hasLocationFilters(locationFilters: LocationFilters): boolean {
    if (!locationFilters) {
      return false;
    }
    return locationFilterKeys.some((key) => key in locationFilters && !!locationFilters[key]);
  },
  lookUpAgentName(agentId: number) {
    return axios
      .get(`/api/agents/${agentId}`)
      .then((result) => {
        return result.data;
      })
      .catch((error) => Promise.reject({ code: utils.getErrorCode(error) }));
  },
  lookUpCompanyName(companyId: number) {
    return axios
      .get(`/api/agencies/${companyId}`)
      .then((result) => {
        return result.data;
      })
      .catch((error) => Promise.reject({ code: utils.getErrorCode(error) }));
  },
  lookUpRegionNames(filters: FiltersType) {
    const url = `/api/regions/lookup?${buildQuery(filters)}`;

    return axios
      .get(url)
      .then((result) => {
        return result.data;
      })
      .catch((error: unknown) => {
        logger.error(`Error fetching region names with url: ${url}`, error);
        return Promise.reject({ code: utils.getErrorCode(error) });
      });
  },
  /**
   * Removes the filters from the state and clears the filters from the url
   */
  removeAllFilters(router: any, route: any) {
    const state = useFilterStore();
    state.setFilters({});

    const newLocation = {
      path: route.path,
      query: {},
    };

    void router.replace(newLocation);
  },
  removeFilterFromPath(router: any, route: any, paramName: string, value?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (route.query[paramName] !== undefined) {
        const newQuery = { ...route.query };

        if (Array.isArray(newQuery[paramName]) && value !== undefined) {
          // Ensure both values are compared as strings
          newQuery[paramName] = newQuery[paramName].filter(
            (paramValue: string) => paramValue.toString() !== value.toString()
          );

          if (newQuery[paramName].length === 0) {
            delete newQuery[paramName];
          }
        } else if (value === undefined || newQuery[paramName].toString() === value.toString()) {
          delete newQuery[paramName];
        }
        void router.replace({ path: route.path, query: newQuery }).then(resolve).catch(reject);
      } else {
        resolve();
      }
    });
  },
  getNumberFiltersFromPath(route: any, key: string): number[] | undefined {
    const val = route.query[key];
    if (val) {
      if (Array.isArray(val)) {
        return val.map((v: string) => parseInt(v, 10)).filter((v: number) => !isNaN(v));
      } else {
        const maybeInt = parseInt(val, 10);
        return !isNaN(maybeInt) ? [parseInt(val, 10)] : [];
      }
    }
  },
  /**
   * Takes a current filter object and updates as input values, validates and applies the updates,
   * and returns a new updated filters object
   * @param currentFilters The original filters object
   * @param updates The updates to apply
   * @param allowMultiple If true, allows multiple values for the same key. Otherwise, the key is replaced
   */
  updateFiltersObj(currentFilters: LocationQuery, updates: any, allowMultiple: boolean = false): any {
    const currentFiltersResult = FiltersSchema.safeParse({ ...currentFilters });
    if (!currentFiltersResult.success) {
      logger.info('Failed to parse current filters', {
        currentFilters,
        errorMessage: currentFiltersResult.error?.message,
      });
      throw new CustomError('Failed to parse current filters', 400);
    }
    const filters = currentFiltersResult.data;
    const updatesResult = FiltersSchema.safeParse(updates);
    if (!updatesResult.success) {
      logger.info('Failed to parse updates', {
        currentFilters,
        updates,
        errorMessage: updatesResult.error?.message,
      });
      throw new CustomError('Failed to parse updates', 400);
    }

    Object.entries(updatesResult.data).forEach(([key, newValue]: [string, any]) => {
      if (newValue === undefined || (Array.isArray(newValue) && newValue.length === 0) || newValue === '') {
        // Removes the key from the query if the value is empty to avoid "empty" keys remaining in the route
        // E.g. If the caller wants to add an empty array for example and doesn't remove the filter instead
        // like in number-of-rooms and listing-types filters
        // @ts-ignore for now
        delete filters[key];
        return;
      }
      if (allowMultiple && filters.hasOwnProperty(key)) {
        // @ts-ignore for now
        const existingValue = filters[key];
        if (Array.isArray(existingValue)) {
          if (!existingValue.includes(newValue.toString())) {
            // @ts-ignore for now
            filters[key] = [...existingValue, newValue];
          }
        } else if (existingValue !== newValue.toString()) {
          // @ts-ignore for now
          filters[key] = [existingValue, newValue];
        }
      } else {
        // @ts-ignore for now
        filters[key] = newValue;
      }
    });

    return filters;
  },
  /**
   * Gets the filters from the url, validates them and sets defaults if wanted
   */
  getValidatedFiltersFromUrl(routePath: string, routeQuery: LocationQuery, useDefaults: boolean = true): FiltersType {
    let filters: FiltersType = {};
    const urlFilters: any = this.mapUrlToRawFilters(routePath, routeQuery);
    const results = SearchUrlSchema.safeParse(urlFilters);
    if (results.success) {
      filters = dropNonSchemaParams(results.data, FiltersSchema);
    } else {
      throw new CustomError('Failed to parse search url: ' + results.error.message, 400);
    }

    if (useDefaults) {
      if (!filters.order) {
        filters.order = config.FILTER_DEFAULTS.order;
      }
      if (!filters.orderBy) {
        filters.orderBy = config.FILTER_DEFAULTS.orderBy;
      }
    }
    return filters;
  },
  /**
   * Takes the filter updates and adds them to the url
   */
  addFilterToUrl(router: Router, route: RouteLike, updates: any, allowMultiple: boolean = false) {
    const query = dropNonSchemaParams(route.query, FiltersSchema);
    void router.push({ query: this.updateFiltersObj(query, updates, allowMultiple) });
  },
  /**
   * Converts the filters from the url into FiltersType as raw values without any validation.
   */
  mapUrlToRawFilters(routePath: string, routeQuery: LocationQuery): FiltersType {
    const pathFilters = this.mapPathToFilters(routePath);
    const queryFilters = { ...routeQuery };
    if (pathFilters.cityId?.length && queryFilters.cityId) {
      // Handle the cityId generated from the /:cityName path
      const cityFilters = pathFilters.cityId;
      if (Array.isArray(queryFilters.cityId)) {
        cityFilters.push(...queryFilters.cityId.filter((cityId) => cityId !== null));
      } else {
        cityFilters.push(queryFilters.cityId);
      }
      return { ...pathFilters, ...queryFilters, cityId: cityFilters };
    } else {
      return { ...pathFilters, ...queryFilters };
    }
  },
  /**
   * Converts the path part of the url (e.g. /myytavat/asunnot/helsinki) to PathFilters (mode, featureGroup, cityId)
   */
  mapPathToFilters(path: any): PathFilters {
    // 1. mode
    // 2. feature group
    // 3. location (cityId)
    const pathParts = utils.splitPathToParts(path);
    const pathFilters: PathFilters = {};

    if (!pathParts.length) {
      return {};
    }
    pathFilters.mode = pathParts[0] === 'myytavat' ? config.SEARCH_MODES.SALE : config.SEARCH_MODES.RENTAL;

    const searchKey = {
      pathName: pathParts[1],
    } as any;

    if (pathFilters.mode === config.SEARCH_MODES.SALE) {
      searchKey.showInHousingOnSale = true;
    } else {
      searchKey.showInRentals = true;
    }

    pathFilters.featureGroup = find(featureGroups, searchKey)?.id;
    const city = Object.keys(cities).find((city) => utils.nameToPathFormat(city) === pathParts[2]);
    if (city) {
      const cityId = [cities[city as keyof typeof cities].toString()];
      if (cityId) {
        pathFilters.cityId = cityId;
      }
    }
    return pathFilters;
  },
  /**
   * Removes those filters that are not used in the given feature group and mode
   */
  dropNonFeatureGroupFilters(path: string, filters: FiltersType) {
    const modifiedFilters = { ...filters };
    const { mode, featureGroup } = this.mapPathToFilters(path);
    if (!featureGroup || !mode) {
      throw new CustomError('Feature group or mode not found in path', 400);
    }
    const featureGroupConfig = getFeatureGroupById(featureGroup);
    const featureGroupFilters = featureGroupConfig?.filters[mode];
    if (featureGroupFilters) {
      // Filter out filters not allowed for the given feature group and mode
      Object.keys(filters).forEach((filterKey) => {
        let featureGroupFilterKey = filterKey as FilterViewType;
        if (filterKey === 'feature') {
          featureGroupFilterKey = 'extraFeatures'; // feature key in url matches the extraFeatures in feature groups
        } else if (filterKey === 'type') {
          featureGroupFilterKey = 'listingTypes'; // type key in url matches the listingTypes in feature groups
        }
        const allFilters = [locationFilterKeys, featureGroupFilters].flat();
        const filterOk = allFilters.includes(featureGroupFilterKey);
        if (!filterOk) {
          delete modifiedFilters[filterKey as keyof FiltersType];
        }
      });
      // For the remaining filters, filter out the non-allowed filter values
      if (modifiedFilters.feature) {
        const featureValues = modifiedFilters.feature;
        const filterdValues = featureValues.filter((value) => {
          const allowedValues = featureGroupConfig.extraFeatures;
          return allowedValues?.includes(value);
        });
        if (!filterdValues.length) {
          delete modifiedFilters.feature;
        } else {
          modifiedFilters.feature = filterdValues;
        }
      }
      if (modifiedFilters.type) {
        const typeValues = modifiedFilters.type;
        const filteredValues = typeValues.filter((value) => {
          const allowedValues = featureGroupConfig.types;
          return allowedValues?.includes(value);
        });
        if (!filteredValues.length) {
          delete modifiedFilters.type;
        } else {
          modifiedFilters.type = filteredValues;
        }
      }
    }
    return modifiedFilters;
  },
};
